import React, { useMemo } from 'react';
import { Option } from '@common-fe/common-fe';
import dayjs from 'dayjs';
import { capitalize } from 'lodash';

import regexp from '@/common/regexp';
import { useDocumentOcrStore } from '@/modules/UseMyMoney/stores';
import { OcrServiceFor } from '@/modules/UseMyMoney/UseMyMoney.types';

import MoreInformationNeededWarning from '../components/ServiceForFieldSetup/MoreInformationNeededWarning';

const MAX_PATIENT_OPTIONS = 3;

const getPatientSubTitle = (patient: OcrServiceFor) => {
  const relationshipTypeTitle = !patient?.relationshipType && !patient?.dependentId ? 'You' : patient?.relationshipType;
  const age = patient?.birthOn ? dayjs().diff(dayjs(patient.birthOn), 'year') : null;
  const formattedRelationshipType = capitalize(relationshipTypeTitle).replace(regexp.DASH_SYMBOL, ' ');
  const ageSuffix = age !== null ? `, ${age} ${age === 1 ? 'year' : 'years'}` : '';
  return `${formattedRelationshipType}${ageSuffix}`;
};

interface Props {
  patientId?: string;
  setIsAddPatientModalVisible?: (value: boolean) => void;
  setProviderFullName?: (value: string) => void;
}

const usePatientOptions = ({ patientId, setIsAddPatientModalVisible, setProviderFullName }: Props) => {
  const { ocrDocument } = useDocumentOcrStore();

  const suggestedPatientOptions = useMemo((): Option<string>[] => {
    if (!ocrDocument?.serviceForOptions?.length) {
      return [];
    }

    const suggestedPatients = ocrDocument?.serviceForOptions?.filter((patient) => !!patient?.isSuggested && patient?.fullName);

    return suggestedPatients
      ?.slice(0, MAX_PATIENT_OPTIONS)
      ?.map((suggestedPatient) => ({
        key: suggestedPatient?.id,
        title: suggestedPatient?.fullName,
        value: suggestedPatient?.id,
        ...(suggestedPatient?.fullName ? { metadata: { full_name: suggestedPatient?.fullName } } : {}),
        ...(suggestedPatient?.id
          ? {}
          : {
              maxItemWidth: '33%',
              gridColumn: '1 / -1',
              node: (
                <MoreInformationNeededWarning
                  margin={{ top: 'spacing8' }}
                  fullName={suggestedPatient?.fullName}
                  setIsAddPatientModalVisible={setIsAddPatientModalVisible}
                  setProviderFullName={setProviderFullName}
                />
              ),
            }),
      }));
  }, [ocrDocument?.serviceForOptions, setIsAddPatientModalVisible, setProviderFullName]);

  const patientOptions = useMemo((): Option<string>[] => {
    if (!ocrDocument?.serviceForOptions?.length) {
      return [];
    }

    const notSuggestedPatients = ocrDocument?.serviceForOptions?.filter((patient) => !patient?.isSuggested && patient?.fullName);

    return notSuggestedPatients
      ?.map((patient) => ({
        key: patient?.id,
        title: patient?.fullName,
        value: patient?.id,
        additionalValue: getPatientSubTitle(patient),
      }));
  }, [ocrDocument?.serviceForOptions]);

  const activePatientOption = useMemo(() => patientOptions?.find((patientOption) => patientOption?.key === patientId),
    [patientOptions, patientId]);

  return {
    suggestedPatientOptions,
    patientOptions,
    activePatientOption,
  };
};

export default usePatientOptions;
